import {
  isShapeToolKind,
  type ShapeToolKind,
  type Style,
} from "@bezpart-board/shared";

export type ShapeStrokeDash = "solid" | "dashed" | "dotted";

export type ShapeToolSettings = {
  kind: ShapeToolKind;
  stroke: string;
  fill: string;
  strokeWidth: number;
  strokeDash?: ShapeStrokeDash;
  /** Рисовать точку в центре плоских фигур. */
  showCenter?: boolean;
};

const STORAGE_KEY = "wb-shape-settings";

const DEFAULT_SETTINGS: ShapeToolSettings = {
  kind: "rect",
  stroke: "#6246ea",
  // Прозрачная: фигуру чаще обводят вокруг написанного, а залитая прячет
  // его под собой. Кому нужна заливка — выбирает её в окошке фигуры.
  fill: "none",
  strokeWidth: 1,
  strokeDash: "solid",
  showCenter: false,
};

function isDash(v: unknown): v is ShapeStrokeDash {
  return v === "solid" || v === "dashed" || v === "dotted";
}

function loadSettings(): ShapeToolSettings {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const p = JSON.parse(raw) as Partial<ShapeToolSettings>;
    return {
      kind: isShapeToolKind(p.kind) ? p.kind : DEFAULT_SETTINGS.kind,
      stroke: typeof p.stroke === "string" ? p.stroke : DEFAULT_SETTINGS.stroke,
      fill: typeof p.fill === "string" ? p.fill : DEFAULT_SETTINGS.fill,
      strokeWidth:
        typeof p.strokeWidth === "number" && Number.isFinite(p.strokeWidth)
          ? p.strokeWidth
          : DEFAULT_SETTINGS.strokeWidth,
      strokeDash: isDash(p.strokeDash) ? p.strokeDash : DEFAULT_SETTINGS.strokeDash,
      showCenter: p.showCenter === true,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function persistSettings(next: ShapeToolSettings): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

let settings: ShapeToolSettings = loadSettings();

export function getShapeSettings(): ShapeToolSettings {
  return settings;
}

export function setShapeSettings(partial: Partial<ShapeToolSettings>): void {
  settings = { ...settings, ...partial };
  persistSettings(settings);
}

/** Пары «контур ↔ заливка» одного оттенка (как у рамок). */
export const SHAPE_COLOR_PAIRS = [
  { stroke: "#6246ea", fill: "rgba(98, 70, 234, 0.15)" },
  { stroke: "#0ea5e9", fill: "rgba(14, 165, 233, 0.15)" },
  { stroke: "#dc2626", fill: "rgba(220, 38, 38, 0.12)" },
  { stroke: "#ca8a04", fill: "#fef08a" },
  { stroke: "#16a34a", fill: "rgba(22, 163, 74, 0.15)" },
  { stroke: "#1a1a2e", fill: "#ffffff" },
] as const;

export const SHAPE_STROKE_PRESETS = [
  ...SHAPE_COLOR_PAIRS.map((p) => p.stroke),
  "#ffffff",
] as const;

/** «Без заливки» первой: это и значение по умолчанию. */
export const SHAPE_FILL_PRESETS = [
  "none",
  ...SHAPE_COLOR_PAIRS.map((p) => p.fill),
] as const;

/** Толщина контура фигур: от волосяной до жирной — просили больше выбора. */
export const SHAPE_WIDTH_PRESETS = [1, 2, 3, 4, 6, 8, 12, 16] as const;

export const SHAPE_DASH_PRESETS: { id: ShapeStrokeDash; label: string; title: string }[] = [
  { id: "solid", label: "─", title: "Сплошная" },
  { id: "dashed", label: "┄", title: "Пунктир" },
  { id: "dotted", label: "···", title: "Точки" },
];

export type ShapeKindOption = {
  id: ShapeToolKind;
  title: string;
  /** Группа в библиотеке фигур */
  group: "basic" | "arrows" | "symbols" | "polygons" | "volume";
};

export const SHAPE_KIND_OPTIONS: ShapeKindOption[] = [
  // базовые
  { id: "rect", title: "Прямоугольник", group: "basic" },
  { id: "rounded_rect", title: "Скруглённый", group: "basic" },
  { id: "ellipse", title: "Эллипс", group: "basic" },
  { id: "stadium", title: "Капсула", group: "basic" },
  { id: "triangle", title: "Треугольник", group: "basic" },
  { id: "diamond", title: "Ромб", group: "basic" },
  { id: "star", title: "Звезда", group: "basic" },
  // стрелки
  { id: "arrow_leftright", title: "Стрелка ↔", group: "arrows" },
  { id: "arrow_left", title: "Стрелка ←", group: "arrows" },
  { id: "arrow_right", title: "Стрелка →", group: "arrows" },
  { id: "arrow_updown", title: "Стрелка ↕", group: "arrows" },
  { id: "arrow_up", title: "Стрелка ↑", group: "arrows" },
  { id: "arrow_down", title: "Стрелка ↓", group: "arrows" },
  { id: "chevron_left", title: "Шеврон ←", group: "arrows" },
  { id: "chevron_right", title: "Шеврон →", group: "arrows" },
  // символы
  { id: "cloud", title: "Облако", group: "symbols" },
  { id: "bubble", title: "Реплика", group: "symbols" },
  { id: "thought", title: "Мысль", group: "symbols" },
  { id: "brace", title: "Скобка", group: "symbols" },
  { id: "heart", title: "Сердце", group: "symbols" },
  { id: "cross", title: "Крест", group: "symbols" },
  { id: "plus", title: "Плюс", group: "symbols" },
  { id: "crescent", title: "Полумесяц", group: "symbols" },
  { id: "banner", title: "Флажок", group: "symbols" },
  { id: "document", title: "Документ", group: "symbols" },
  { id: "ring", title: "Кольцо", group: "symbols" },
  // многоугольники
  { id: "hexagon", title: "Шестиугольник", group: "polygons" },
  { id: "pentagon", title: "Пятиугольник", group: "polygons" },
  { id: "octagon", title: "Восьмиугольник", group: "polygons" },
  { id: "right_triangle", title: "Прямоугольный △", group: "polygons" },
  { id: "left_triangle", title: "Прямоугольный ◁", group: "polygons" },
  { id: "trapezoid", title: "Трапеция", group: "polygons" },
  { id: "parallelogram", title: "Параллелограмм", group: "polygons" },
  { id: "parallelogram_alt", title: "Параллелограмм ↘", group: "polygons" },
  { id: "rhomboid", title: "Ромбоид", group: "polygons" },
  // объём
  { id: "sphere", title: "Сфера", group: "volume" },
  { id: "frustum", title: "Усеч. конус", group: "volume" },
  { id: "cone", title: "Конус", group: "volume" },
  { id: "cylinder", title: "Цилиндр", group: "volume" },
  { id: "pyramid", title: "Пирамида", group: "volume" },
  { id: "pyramid_tri", title: "Тетраэдр", group: "volume" },
  { id: "box", title: "Параллелепипед", group: "volume" },
  { id: "oblique_box", title: "Наклонный параллел.", group: "volume" },
];

export const SHAPE_KIND_GROUPS: {
  id: ShapeKindOption["group"];
  title: string;
}[] = [
  { id: "basic", title: "Базовые" },
  { id: "arrows", title: "Стрелки" },
  { id: "symbols", title: "Символы" },
  { id: "polygons", title: "Многоугольники" },
  { id: "volume", title: "Объёмные" },
];

export function shapeSettingsEventName(): string {
  return "wb-shape-settings";
}

export function shapeStyleFields(settings: ShapeToolSettings): Style {
  return {
    stroke: settings.stroke,
    strokeWidth: settings.strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeDash: settings.strokeDash ?? "solid",
    fill: settings.fill,
  };
}
